import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BookOpen, Film, LoaderCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { useLab } from '../lib/service';
import { label } from '../shared';
import type { ProjectView } from '../../../../packages/domain/src/view';

export function Status({ value }: { value: string }) {
  return (
    <span className={`status status-${value.toLowerCase()}`}>
      <i />
      {label(value)}
    </span>
  );
}
export function Logo() {
  return (
    <span className="brand">
      <img src="/brand/claptal.png" alt="" width={23} height={32} />
      <span>
        claptal<span className="brand-dot">.</span>
        <small>Drama Lab</small>
      </span>
    </span>
  );
}
export function Modal({
  title,
  subtitle,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement;
    ref.current?.querySelector<HTMLElement>('input,textarea,button')?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeRef.current();
      if (e.key === 'Tab') {
        const nodes = ref.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled),input:not(:disabled),textarea:not(:disabled),select:not(:disabled),a[href]',
        );
        if (!nodes?.length) return;
        const first = nodes[0],
          last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus();
    };
  }, []);
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`modal ${wide ? 'modal-wide' : ''}`}
      >
        <div className="modal-heading">
          <div>
            <span className="eyebrow">CLAPTAL LAB</span>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close dialog">
            <X size={20} />
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
}
export function Poster({
  project,
  small = false,
}: {
  project: Pick<ProjectView, 'title' | 'genre'>;
  small?: boolean;
}) {
  const tone = /romance/i.test(project.genre)
    ? 'romance'
    : /family/i.test(project.genre)
      ? 'family'
      : /comedy/i.test(project.genre)
        ? 'comedy'
        : 'mystery';
  return (
    <div
      className={`poster poster-${tone} ${small ? 'poster-small' : ''}`}
      aria-label={`${project.title} concept artwork`}
    >
      <span className="poster-monogram" aria-hidden="true">
        {project.title[0]}
      </span>
      <div className="poster-shade" />
      <div className="poster-top">
        <span>CLAPTAL / CONCEPT</span>
      </div>
      <div className="poster-title">
        <span>{project.genre}</span>
        <h3>{project.title}</h3>
      </div>
      <span className="artwork-label">Artwork not generated yet</span>
    </div>
  );
}
export function Loading() {
  return (
    <div className="loading">
      <LoaderCircle className="spin" size={24} />
      <span>Opening the production room…</span>
    </div>
  );
}
export function Media({
  assetId,
  video = false,
  captionsId,
  placeholder,
}: {
  assetId?: string;
  video?: boolean;
  captionsId?: string;
  placeholder?: string;
}) {
  const { url, isDemo } = useLab();
  const [src, setSrc] = useState(''),
    [captionSrc, setCaptionSrc] = useState(''),
    [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    setSrc('');
    setCaptionSrc('');
    setError('');
    if (assetId)
      void Promise.all([url(assetId), captionsId ? url(captionsId) : Promise.resolve('')])
        .then(([v, captions]) => {
          if (live) {
            setSrc(v);
            setCaptionSrc(captions);
          }
        })
        .catch((e) => {
          if (live) setError(String(e));
        });
    return () => {
      live = false;
    };
    // Deliberate: `url` is rebuilt on every provider render, so depending on it would re-sign the asset URL continuously.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, captionsId]);
  if (!assetId || (!src && !error))
    return (
      <div className="media-placeholder">
        <Film size={27} />
        <span>
          {placeholder ??
            (video
              ? 'Video preview pending'
              : isDemo
                ? 'Simulated identity reference'
                : 'Reference will appear here')}
        </span>
      </div>
    );
  if (error)
    return (
      <div className="notice error">
        Unable to load this private asset. Refresh to renew access.
      </div>
    );
  return video ? (
    <video controls src={src} crossOrigin="anonymous" preload="metadata" className="video-player">
      {captionSrc && (
        <track kind="captions" src={captionSrc} srcLang="en" label="English" default />
      )}
    </video>
  ) : (
    <img src={src} alt="Generated actor or shot reference" className="reference-image" />
  );
}
export function Empty({ title, text }: { title: string; text: string }) {
  return (
    <div className="empty-state">
      <BookOpen size={30} />
      <h2>{title}</h2>
      <p>{text}</p>
    </div>
  );
}
